<template>
    <FlatPickr v-model="internalValue" :config="dateTimeConfig" />
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import FlatPickr from "vue-flatpickr-component";
    import type { DateOption, Options } from "flatpickr/dist/types/options";

    import type { CustomLocale } from "flatpickr/dist/types/locale";
    import { Czech } from "flatpickr/dist/l10n/cs.js";
    import { French } from "flatpickr/dist/l10n/fr.js";
    import { German } from "flatpickr/dist/l10n/de.js";
    import { Hungarian } from "flatpickr/dist/l10n/hu.js";
    import { Italian } from "flatpickr/dist/l10n/it.js";
    import { Polish } from "flatpickr/dist/l10n/pl.js";
    import { Portuguese } from "flatpickr/dist/l10n/pt.js";
    import { Russian } from "flatpickr/dist/l10n/ru.js";
    import { Spanish } from "flatpickr/dist/l10n/es.js";
    import { Ukrainian } from "flatpickr/dist/l10n/uk.js";
    import "flatpickr/dist/flatpickr.css";
    import "flatpickr/dist/themes/airbnb.css";

    type DatePickerValue = DateOption | DateOption[] | null;

    const props = defineProps<{
        modelValue: DatePickerValue;
        enableTime?: boolean | string;
        minDate?: DateOption | null;
        maxDate?: DateOption | null;
        static?: boolean | string;
    }>();

    const emit = defineEmits<{
        "update:modelValue": [value: DatePickerValue];
    }>();

    // Fallback to en as default. E.g. in super admin.
    const locales: Record<string, CustomLocale> = {
        cs: Czech,
        de: German,
        es: Spanish,
        fr: French,
        hu: Hungarian,
        it: Italian,
        pl: Polish,
        pt: Portuguese,
        ru: Russian,
        uk: Ukrainian,
    };
    const localeToUse = locales[window.panelLocale];

    const internalValue = computed({
        get: () => props.modelValue,
        set: value => emit("update:modelValue", value),
    });

    const dateTimeConfig = computed<Options>(() => {
        const options: Options = {
            minDate: props.minDate ?? undefined,
            maxDate: props.maxDate ?? undefined,
            // Scrolling issue: https://github.com/flatpickr/flatpickr/issues/1160
            static: !!props.static,
            // Flatpickr's native mobile fallback (datetime-local input) only sets min/max
            // to a date (no time-of-day), so e.g. single-day events end up with min == max == midnight,
            // making it impossible to pick any other time on mobile.
            disableMobile: true,
        };

        if (props.enableTime) {
            options.enableTime = true;
            options.dateFormat = "Y-m-d H:i:S";
            options.time_24hr = true;
        }

        if (localeToUse) {
            options.locale = localeToUse;
        }

        return options;
    });
</script>
